import DesignTokens from '@tableau/design-tokens';
import { TabStyles } from '@tableau/widgets';

import { CommonStyles } from '~src/commonInfra/styles/CommonStyles';
import { applyColor }from '~src/style/ColorService';

import { cssClass } from '../../style';

const cssPrefix = 'GuidedOnboarding';

const WELCOME_MESSAGE_CONTAINER_WIDTH = 300;
const CARD_WIDTH = 200;
const CARD_HEIGHT = 150;
const CARD_MULTIPLIER = 1.3;

export namespace GuidedOnboardingStyles {

    const SpacingUnit = 8;

    const Spacing = {
        DefaultSpace: SpacingUnit,
        MediumSpace: SpacingUnit * 2,
        LargeSpace: SpacingUnit * 3,
        ExtraLargeSpace: SpacingUnit * 4
    };

    const Colors = {
        WelcomeBannerBackground: applyColor(DesignTokens.color.onboarding.background.default.banner_default, 'rgb(240, 248, 255)'),
        WelcomeBannerBorder: applyColor(DesignTokens.color.onboarding.border.default.banner_default, 'rgb(227, 242, 255)'),
        CloseButtonBackground: applyColor(DesignTokens.color.elevation.surface.default.default, 'rgba(250, 250, 250, 1)'),
        PaginationButtonBackground: applyColor(DesignTokens.color.mode_agnostic.default.neutral40, 'rgb(16, 124, 173)'),
        PaginationButtonHoverBackground: applyColor(DesignTokens.color.mode_agnostic.default.neutral80, 'rgb(13, 107, 138)'),
        PaginationButtonShadow: 'rgba(0, 0, 0, 0.11)'
    };

    export const welcomeBanner = cssClass(cssPrefix, 'welcome-banner', {
        display: 'flex',
        margin: Spacing.LargeSpace,
        padding: `${Spacing.MediumSpace}px 0 ${Spacing.MediumSpace}px ${Spacing.MediumSpace}px`,
        gap: Spacing.LargeSpace,
        borderRadius: Spacing.LargeSpace,
        border: `1px solid ${Colors.WelcomeBannerBorder}`,
        backgroundColor: Colors.WelcomeBannerBackground,
        position: 'relative'
    });

    export const bannerMessageWrapper = cssClass(cssPrefix, 'banner-message-wrapper', {
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'flex-start',
        padding: `${Spacing.MediumSpace}px 0 0 ${Spacing.MediumSpace}px`,
        width: WELCOME_MESSAGE_CONTAINER_WIDTH,
        maxHeight: CARD_HEIGHT + Spacing.MediumSpace,
        overflow: 'hidden'
    });

    export const bannerMessage = cssClass(cssPrefix, 'banner-message', {
        maxHeight: CARD_HEIGHT + Spacing.MediumSpace,
        overflow: 'hidden'
    });

    export const bannerMessageHeader = cssClass(cssPrefix, 'banner-message-header', {
        fontSize: TabStyles.Typography.Sizing.XXLarge,
        fontWeight: 350,
        color: applyColor(DesignTokens.color.component.content.neutral.default.default, TabStyles.Colors.Black),
        marginTop: 0,
        lineHeight: `${Spacing.ExtraLargeSpace}px`,
        wordWrap: 'break-word'
    });

    export const bannerMessageSubheader = cssClass(cssPrefix, 'banner-message-subheader', {
        fontSize: TabStyles.Typography.Sizing.Large,
        fontWeight: 350,
        color: applyColor(DesignTokens.color.component.content.neutral.default.default, TabStyles.Colors.Black),
        letterSpacing: '0.2px',
        wordWrap: 'break-word'
    });

    export const viewAllButtonBelow = cssClass(cssPrefix, 'view-all-button-below', {
        marginTop: Spacing.DefaultSpace,
        display: 'inline-block',
        alignSelf: 'flex-start'
    });

    // Modal styles
    export const modalOverlay = cssClass(cssPrefix, 'modal-overlay', {
        position: 'fixed',
        top: 0,
        left: 0,
        right: 0,
        bottom: 0,
        backgroundColor: 'rgba(0, 0, 0, 0.5)', // it's for the half transparent effect, no need to use token.
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        zIndex: 1000
    });

    export const modalContainer = cssClass(cssPrefix, 'modal-container', {
        backgroundColor: applyColor(DesignTokens.color.elevation.surface.default.default, TabStyles.Colors.White),
        borderRadius: '16px',
        border: '1px solid '+ applyColor(DesignTokens.color.elevation.border.default.subtle, '#BEBEBE'),
        boxShadow: '0px 4px 4px '+ applyColor(DesignTokens.color.feedback.default.neutral, 'rgba(0, 0, 0, 0.25)'),
        width: '1096px',
        height: '756px',
        display: 'flex',
        flexDirection: 'column',
        overflow: 'hidden'
    });

    export const modalHeader = cssClass(cssPrefix, 'modal-header', {
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center',
        padding: '32px 40px 8px 40px'
    });

    export const modalTitle = cssClass(cssPrefix, 'modal-title', {
        fontSize: '24px',
        fontWeight: 500,
        color: applyColor(DesignTokens.color.component.content.neutral.default.default, TabStyles.Colors.Black),
        margin: 0,
        letterSpacing: '0.2px'
    });

    export const modalCloseButton = cssClass(cssPrefix, 'modal-close-button', {
        backgroundColor: 'transparent',
        border: 'none',
        fontSize: '24px',
        color: applyColor(DesignTokens.color.font.default.default, '#2e2e2e'),
        cursor: 'pointer',
        padding: '4px',
        borderRadius: '4px',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        width: '32px',
        height: '32px',
        $nest: {
            '&:hover': {
                backgroundColor: 'rgba(0, 0, 0, 0.05)', // it's for the half transparent effect, no need to use token.
            },
            '&:focus': {
                ...CommonStyles.Focus.OutlineIndicator,
                outlineOffset: CommonStyles.Focus.IndicatorWidth,
            }
        }
    });

    export const modalContent = cssClass(cssPrefix, 'modal-content', {
        flex: 1,
        padding: '24px 40px 32px 40px',
        overflowY: 'auto'
    });

    export const modalGrid = cssClass(cssPrefix, 'modal-grid', {
        display: 'grid',
        gridTemplateColumns: 'repeat(auto-fill, minmax(230px, 1fr))',
        gap: '24px 32px',
        justifyContent: 'space-between'
    });

    export const filmStripContainer = cssClass(cssPrefix, 'cards-film-strip-container', {
        display: 'flex',
        alignItems: 'center',
        flex: '1 0 0',
        minWidth: `${CARD_WIDTH * CARD_MULTIPLIER}px`,
        overflow: 'hidden',
        position: 'relative'
    });

    export const filmStripContentWrapper = cssClass(cssPrefix, 'film-strip-content-wrapper', {
        flex: '1 1 auto',
        overflow: 'hidden',
        position: 'relative'
    });

    export const filmStripContentBody = cssClass(cssPrefix, 'film-strip-content-body', {
        display: 'flex',
        overflow: 'hidden',
        scrollBehavior: 'smooth',
        paddingRight: Spacing.MediumSpace,
        gap: Spacing.MediumSpace
    });

    export const filmStripPaginationButton = cssClass(cssPrefix, 'film-strip-pagination-button', {
        position: 'absolute',
        top: '50%',
        transform: 'translateY(-50%)',
        zIndex: 1,
        width: '40px',
        height: '40px',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        // The chevron icon renders with fill="currentColor"; the original glyph was fill="white",
        // so drive currentColor to white here to keep the chevron white on the blue button.
        color: TabStyles.Colors.White,
        background: Colors.PaginationButtonBackground,
        border: 'none',
        borderRadius: '20px',
        cursor: 'pointer',
        boxShadow: `0 7px 11px 0 ${applyColor(DesignTokens.color.elevation.surface.default.default, Colors.PaginationButtonShadow)}`,
        transition: 'all 0.2s ease-in-out',
        $nest: {
            '&:hover': {
                background: Colors.PaginationButtonHoverBackground
            }
        },
    });

    export const filmStripPrevButton = cssClass(cssPrefix, 'film-strip-prev-button', {
        left: Spacing.DefaultSpace,
    });

    export const filmStripNextButton = cssClass(cssPrefix, 'film-strip-next-button', {
        right: Spacing.DefaultSpace,
    });

    export const closeButton = cssClass(cssPrefix, 'close_button', {
        position: 'absolute',
        right: -CommonStyles.Sizes.MediumSpace,
        top: -CommonStyles.Sizes.MediumSpace,
        alignItems: 'center',
        display: 'inline-flex',
        justifyContent: 'center',
        borderRadius: 20,
        $nest: {
            '&': { minHeight: 32, minWidth: 32, height: 32, width: 32, padding: 0 },
        },
        backgroundColor: Colors.CloseButtonBackground
    });

    export const welcomeBannerContainer = cssClass(cssPrefix, 'welcome-banner-container', {
        transition: 'height 0.5s',
        overflow: 'hidden'
    });

    export const cardWrapper = cssClass(cssPrefix, 'card-wrapper', {
        border: '1px solid transparent',
        borderRadius: Spacing.DefaultSpace,
        $nest: {
            '&:focus': {
                border: `1px solid ${applyColor(DesignTokens.color.utility.focus.default.default, TabStyles.Colors.Focus)}`,
            }
        }
    });

    export const cardContainer = cssClass(cssPrefix, 'card-container', {
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        width: `${CARD_WIDTH}px`,
        height: `${CARD_HEIGHT}px`,
        padding: `${Spacing.MediumSpace}px`,
        borderRadius: `${Spacing.DefaultSpace}px`,
        border: `1px solid ` + applyColor(DesignTokens.color.elevation.border.default.default, 'rgb(196, 196, 196)'),
        background: applyColor(DesignTokens.color.elevation.surface.default.default, TabStyles.Colors.White),
        $nest: {
            '&:hover': {
                border: `1px solid ${applyColor(DesignTokens.color.utility.focus.default.default, TabStyles.Colors.Focus)}`
            }
        }
    });

    export const cardContent = cssClass(cssPrefix, 'card-content', {
        display: 'flex',
        flexDirection: 'column',
        justifyContent: 'space-between',
        height: '100%',
        width: '100%'
    });

    export const cardMain = cssClass(cssPrefix, 'card-main', {
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'flex-start',
        overflow: 'hidden',
        flex: '1 1 auto'
    });

    export const cardDescription = cssClass(cssPrefix, 'card-description', {
        margin: 0
    });

    export const cardFooter = cssClass(cssPrefix, 'card-footer', {
        display: 'flex',
        paddingTop: `${Spacing.DefaultSpace}px`,
        flexDirection: 'column',
        alignItems: 'flex-start'
    });

    export const cardTypeContainer = cssClass(cssPrefix, 'card-type-container', {
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center',
        width: '100%'
    });

    export const cardType = cssClass(cssPrefix, 'card-type', {
        display: 'flex',
        alignItems: 'center',
        gap: `${Spacing.DefaultSpace}px`
    });

    export const cardContainerCompleted = cssClass(cssPrefix, 'card-container-completed', {
        background: applyColor(DesignTokens.color.onboarding.background.default.tile_completed, 'rgb(232, 244, 253)'),
        $nest: {
            '&:hover': {
                border: `1px solid ${applyColor(DesignTokens.color.utility.focus.default.default, TabStyles.Colors.Focus)}`
            }
        }
    });

    export const cardCheckmarkCircle = cssClass(cssPrefix, 'card-checkmark-circle', {
        position: 'absolute',
        right: 12,
        bottom: 12,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        width: 32,
        height: 32,
        borderRadius: '50%',
        background: applyColor(DesignTokens.color.mode_agnostic.default.sky30, '#184B6B'),
    });

    export const onboardingSpinnerContainer = cssClass(cssPrefix, 'onboarding-spinner-container', {
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center',
        minHeight: 200,
        width: '100%',
    });
}
